import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import Clarity from '@microsoft/clarity';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { useEffect, useMemo } from 'react';

import {
  PageLayout,
  SimpleButton,
  ArrowIcon,
  disableUIBlocker,
  BoldHeading,
} from '@/components/common';
import { RedirectStatus } from '@/components/contexts/params/schemas/RedirectStatus';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { triggerErrorToast } from '@/libs/toasts';
import BankTransferService from '@/services/banktransfer/BankTransferService';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { formatPrice } from '@/utils/formatters';

import { BankTransferDetails } from './BankTransferDetails';
import { PayButton } from '../stored-instruments/utils/PayButton';

const styles = {
  loadingSpinner: 'flex items-center justify-center',
  pageContent: 'p-sm md:p-md 2xl:p-lg',
  pageContentHeader:
    'sticky top-0 bg-primary p-sm md:p-md 2xl:p-lg z-50 text-white',
  pageContentNavigation: 'flex',
  pageContentFooter: 'mt-md flex w-full justify-between',
  pageStickyContainer:
    'sticky bottom-0 border-0 z-50 bg-transparent pb-xs pt-sm',
  pageInnerStickyContainer: 'flex justify-center',
};

export function BankTransferDetailsPage() {
  const { accessToken } = useAuth();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const paymentModuleT = useUserFriendlyPaymentModuleName();

  useEffect(() => {
    disableUIBlocker();
  }, []);

  const headerText = useMemo(() => {
    return t('Total: {{totalPrice}}', {
      totalPrice: formatPrice(
        language as SupportedLanguage,
        PaymentStore.currency,
        PaymentStore.orderTotal,
      ),
    });
  }, [t, language]);

  return (
    <PageLayout
      headerDescription={
        PaymentStore.orderNumber
          ? t('Order: {{orderNumber}}', {
              orderNumber: PaymentStore.orderNumber,
            })
          : undefined
      }
    >
      <div className={styles.pageContentHeader}>
        <BoldHeading>{headerText}</BoldHeading>
      </div>
      <div className="m-sm md:m-md">
        <div>
          <SimpleButton
            onClick={() =>
              NavigationService.navigateTo({
                page: InternalPage.PaymentMethods,
              })
            }
          >
            <span className={'flex flex-row items-center justify-center'}>
              <ArrowIcon direction="left" />
              <span className={'ml-xs'}>{t('Back')}</span>
            </span>
          </SimpleButton>
        </div>
        <br />
        <BankTransferDetails />
        <br />
        <div className={'flex flex-row items-center justify-center wrap'}>
          <br />
          <PayButton
            title={t('Proceed')}
            onClick={() => {
              const currentPage = NavigationService.getInternalPage();
              NavigationService.navigateTo({
                page: InternalPage.StatusProcessing,
              });
              BankTransferService.authorise(accessToken ?? '')
                .then(() => {
                  NavigationService.navigateTo({
                    page: InternalPage.BankTransferStatusSuccess,
                  });
                  Clarity.setTag('pay', [
                    RedirectStatus.Success,
                    paymentModuleT(PaymentModuleId.BankTransfer),
                  ]);
                })
                .catch((err) => {
                  NavigationService.navigateTo(currentPage);
                  TelemetryService.trackException(
                    err,
                    'BankTransferDetailsPage',
                  );
                  Logger.error(
                    LoggerOrigin.ReactComponent,
                    'Bank transfer failed',
                    err,
                  );
                  triggerErrorToast(
                    t('Something went wrong!'),
                    t('Failed to authorize Bank transfer.'),
                  );
                  Clarity.setTag('pay', [
                    RedirectStatus.Failure,
                    paymentModuleT(PaymentModuleId.BankTransfer),
                  ]);
                });
            }}
          />
        </div>
      </div>
    </PageLayout>
  );
}
